import React, { useContext, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import DashboardIcon from "@material-ui/icons/Dashboard";
import SearchIcon from "@material-ui/icons/Search";
import CallIcon from "@material-ui/icons/Call";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import Tooltip from '@material-ui/core/Tooltip';
import { green } from '@material-ui/core/colors';
import CreateKanbanCardModal from "../CreateKanbanCardModal";
import useCloseTicketGuard from "../../hooks/useCloseTicketGuard";
import useTicketCloseRules from "../../hooks/useTicketCloseRules";
import useToolbarOverflow from "../../hooks/useToolbarOverflow";
import useAssumeTicketOnCall from "../../hooks/useAssumeTicketOnCall";
import { useCall } from "../../context/Call/CallContext";
import { isInactiveTicket } from "../../helpers/canAccessTicket";
import { isCallableWhatsapp } from "../../helpers/callableWhatsapp";


// Tema só para deixar o botão de resolver (✓) verde. Fica em escopo de módulo
// porque não depende de nada do componente — dentro do corpo ele era recriado a
// cada render, remontando o ThemeProvider à toa.
// CUIDADO: é um createTheme "nu", sem espalhar o tema do app, então as chaves
// customizadas (theme.mode, palette.tabHeaderBackground, scrollbarStyles) são
// undefined dentro dele. Não colocar mais nada aqui dentro.
const customTheme = createTheme({
	palette: {
		primary: green,
	}
});

// IconButton: padding 12 + ícone 24 + padding 12 = 48 (IconButton.js:34-40, sem
// borda). Mais o `& > *: { margin: 4 }` abaixo; margens não colapsam em flex,
// então 4 + 4 = 8.
const SLOT_WIDTH = 56;
// classes.actionButtons.marginRight
const TOOLBAR_MARGIN_RIGHT = 6;
// Piso do bloco de contato antes de recolher o primeiro botão:
//   12+12  padding horizontal do CardHeader compacto (TicketHeader/compactStyles)
//   32+12  avatar 32x32 + marginRight 12
//    ~90   piso do nome (~13 caracteres em Roboto 14px, body2)
//    ~42   o "#5699", que TicketInfo fixa e nunca deixa virar reticências
const MIN_INFO_WIDTH = 200;
// Resolver (✓) e o "..." nunca recolhem: o primeiro é a ação principal da tela,
// o segundo é o próprio destino do que recolhe (se sumisse, o Menu ficaria sem
// âncora e renderizaria no canto superior esquerdo).
const PINNED_SLOTS = 2;

// Ordem em que os botões cedem espaço: o primeiro da lista é o primeiro a ir
// para o menu "...".
const COLLAPSE_ORDER = ["kanban", "search", "return", "transfer", "call"];

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: TOOLBAR_MARGIN_RIGHT,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(0.5),
		},
	},
}));

// isModal/onCloseModal: quando o chat está dentro do TicketModal (Fluxos,
// Pendentes, Relatório de Mensagens, Conferência, Monitor de Atendimento) esta
// barra não pode navegar — ver finishStatusChange. Default false para não mudar
// nada em quem não passa as props (ContactChat).
// assumeOnCall: ligar assume o chamado como o envio de mensagem (só a tela do
// chamado, a mesma que passa assumeOnSend ao MessageInput).
const TicketActionButtonsCustom = ({ ticket, onSearchClick, onTicketUpdated, isModal = false, onCloseModal, assumeOnCall = false }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [kanbanModalOpen, setKanbanModalOpen] = useState(false);
	// Só decide se o ícone/item "Criar Card no Kanban" aparece; a exigência do
	// card ao finalizar fica no useTicketCloseRules.
	const [kanbanCardCreationMode, setKanbanCardCreationMode] = useState("optional");
	// Reabrir só é oferecido quando a unificação de conversas permite. Sem isso o
	// botão aparecia e falhava com "Já existe um chamado aberto para este contato"
	// — inclusive quando o atendimento em andamento é de outro usuário.
	const [canReopen, setCanReopen] = useState(true);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	// Aviso/bloqueio de finalização sem interação com o contato (config da empresa)
	const { closeTicket, dialog: closeGuardDialog } = useCloseTicketGuard();
	// Regras que precisam de resposta antes de finalizar (pergunta de conversão,
	// card no Kanban obrigatório/opcional) — as mesmas do menu de contexto da lista
	const { prepareClose, dialogs: closeRulesDialogs } = useTicketCloseRules();

	// Só recolhe em "open": é o único estado com o menu "..." para hospedar o
	// que sai da barra. Em pending/closed os botões são de texto ("Aceitar",
	// "Resolver", "Reabrir"), são a ação principal da tela e cabem folgados —
	// e o min-width:0 do cabeçalho já garante que nada é cortado lá.
	const canCollapse = ticket.status === "open";

	// Na aba de Contatos sem chamado aberto, o ContactChat monta um pseudo-chamado
	// com id "contact-<id>" (mesma convenção de MessageInputCustom). Ele tem
	// contato de verdade, mas nenhuma linha em Tickets — então tudo que depende do
	// id (reabrir, card no Kanban, solicitação de desenvolvimento) não vale ali,
	// só o que age sobre o contato.
	const hasRealTicket = Boolean(ticket?.id) && !/^contact-/.test(String(ticket.id));
	// Botão Transferir no chamado em Aguardando (setting transferPendingTickets).
	// Vem do SettingsContext, que acompanha a configuração ao vivo pelo socket.
	const { getSetting } = useContext(SettingsContext);
	const canTransferPending =
		ticket.status === "pending" &&
		hasRealTicket &&
		getSetting("transferPendingTickets") === "enabled";
	// Ticket de conexão excluída é só leitura: não reabre nem liga.
	const isInactive = isInactiveTicket(ticket);
	// Ligar pelo WhatsApp: só canal com provedor de ligação (Cloud API; Baileys
	// em teste com REACT_APP_BAILEYS_CALLS), conversa individual e perfil com
	// "tickets_ligar" (canUseCalls). Vale também no chamado finalizado: com
	// assumeOnCall, ligar reabre e assume (useAssumeTicketOnCall).
	const { canUseCalls, startCallFromTicket, call } = useCall();
	const { assumeTicketForCall } = useAssumeTicketOnCall({ isModal, onTicketUpdated });
	const showCall =
		canUseCalls &&
		hasRealTicket &&
		!isInactive &&
		!ticket.isGroup &&
		isCallableWhatsapp(ticket.whatsapp) &&
		(ticket.status === "open" || ticket.status === "pending" || ticket.status === "closed");
	const inCallWithTicket =
		Boolean(call && call.phase !== "ended") && Number(call.ticketId) === Number(ticket.id);
	const handleStartCall = () =>
		startCallFromTicket(ticket, assumeOnCall ? { prepareTicket: assumeTicketForCall } : undefined);
	const { ref: toolbarRef, slots } = useToolbarOverflow({
		slot: SLOT_WIDTH,
		reserve: MIN_INFO_WIDTH,
		trailing: TOOLBAR_MARGIN_RIGHT,
		enabled: canCollapse,
	});

	// Esta árvore é trocada sozinha: quando um ticket real nasce no ContactChat,
	// quando o atendimento fecha (history.push) ou por evento de socket. Sem isto
	// as requisições em voo caem num componente já desmontado.
	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		const loadKanbanSettings = async () => {
			try {
				const response = await api.get("/settings");
				if (!isMountedRef.current) return;

				const settings = response.data;
				const kanbanSetting = settings.find(s => s.key === "kanbanCardCreationMode");
				if (kanbanSetting) {
					setKanbanCardCreationMode(kanbanSetting.value);
				}
			} catch (err) {
				console.error("Erro ao carregar configurações do Kanban", err);
			}
		};
		loadKanbanSettings();
	}, []);

	// Pergunta ao backend (mesma regra do CheckContactOpenTickets) se este chamado
	// fechado pode ser reaberto. Falha na checagem mantém o botão: quem decide de
	// fato é o PUT, e esconder a ação por um erro de rede seria pior.
	// Ticket de conexão excluída nunca reabre: nem pergunta ao backend.
	useEffect(() => {
		if (!ticket?.id || ticket?.status !== "closed" || !hasRealTicket) {
			setCanReopen(true);
			return undefined;
		}

		if (isInactive) {
			setCanReopen(false);
			return undefined;
		}

		let cancelled = false;

		(async () => {
			try {
				const { data } = await api.get(`/tickets/${ticket.id}/reopen-availability`);
				if (!cancelled) {
					setCanReopen(data?.canReopen !== false);
				}
			} catch (err) {
				if (!cancelled) {
					setCanReopen(true);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [ticket?.id, ticket?.status, hasRealTicket, isInactive]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
	};

	const handleCloseTransferModal = () => {
		setTransferTicketModalOpen(false);
	};

	const handleOpenKanbanModal = () => {
		setKanbanModalOpen(true);
	};

	const handleCloseKanbanModal = () => {
		setKanbanModalOpen(false);
	};

	// Desfecho de uma ação de status, o único ponto desta barra que decide para
	// onde o usuário vai.
	// Tela cheia: o chamado sai da tela — volta para a lista, ou abre o próprio
	// chamado quando ele passa a estar em atendimento.
	// Dentro do popup: ninguém navega. Trocar a rota desmontaria a tela por baixo
	// (o quadro de Fluxos, a lista de Pendentes...) e o popup, cujo estado vive
	// nela, sumiria junto. A conversa continua na frente do usuário com o novo
	// status; só o encerramento fecha o popup, devolvendo a tela de trás.
	const finishStatusChange = status => {
		if (isModal) {
			if (status === "closed") onCloseModal?.();
			return;
		}

		if (status === "open") {
			// Usa `ticket`, não a resposta do PUT: o TicketsContext navega por
			// currentTicket.uuid, e é o objeto local que garante que ele existe.
			setCurrentTicket({ ...ticket, code: "#open" });
			return;
		}

		setCurrentTicket({ id: null, code: null });
		history.push("/tickets");
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		try {
			if (status === "closed") {
				// Pergunta de conversão e card no Kanban (obrigatório/opcional) vêm
				// antes do PUT; proceed=false é o atendente cancelando em alguma etapa
				const { proceed, converted } = await prepareClose(ticket);
				if (!isMountedRef.current || !proceed) return;

				setLoading(true);
				// O guarda cuida do aviso de "sem interação" e do PUT
				const closed = await closeTicket(ticket.id, {
					userId: userId || null,
					useIntegration: false,
					promptId: false,
					integrationId: false,
					...(converted ? { converted } : {})
				});

				if (isMountedRef.current) setLoading(false);
				if (!closed) return;

				finishStatusChange("closed");
				return;
			}

			setLoading(true);
			const { data } = await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? false : ticket.promptId,
				integrationId: status === "closed" ? false : ticket.integrationId,
			});

			// Reflete o novo status na hora (ex.: o botão REABRIR some ao reabrir),
			// sem depender do evento de socket chegar.
			onTicketUpdated?.(data);

			if (isMountedRef.current) setLoading(false);
			finishStatusChange(status);
		} catch (err) {
			if (isMountedRef.current) setLoading(false);
			toastError(err);
		}
	};

	const showSearch = typeof onSearchClick === "function";
	const showKanban = kanbanCardCreationMode !== "hidden";

	// O "..." também aparece com o chamado fechado: o que sobra no menu age sobre
	// o contato/registro (vincular a cliente, agendar, solicitar desenvolvimento,
	// criar card), não sobre o andamento do atendimento. Quem filtra o resto é o
	// próprio TicketOptionsMenu. Em "pending" continua sem menu.
	const showOptionsMenu = ticket.status === "open" || ticket.status === "closed";

	// Quais botões recolhíveis estão de fato na tela agora
	const collapsible = canCollapse
		? COLLAPSE_ORDER.filter(key => {
			if (key === "search") return showSearch;
			if (key === "kanban") return showKanban;
			if (key === "call") return showCall;
			return true; // transfer e return sempre existem em "open"
		})
		: [];

	// slots é Infinity enquanto não há medição -> nada recolhe. Os que sobram
	// saem do FIM da lista (os últimos a ceder espaço), então o que recolhe são
	// os primeiros de COLLAPSE_ORDER.
	const visibleCollapsible = Math.max(
		0,
		Math.min(collapsible.length, slots - PINNED_SLOTS)
	);
	const hidden = new Set(collapsible.slice(0, collapsible.length - visibleCollapsible));

	// O menu já tem um item "Transferir" próprio (TicketOptionsMenu), então
	// recolher esse ícone não acrescenta linha nenhuma — senão apareceriam duas
	// entradas idênticas.
	// Com o chamado fechado a barra não recolhe (canCollapse é false, `hidden`
	// fica vazio) e o ícone do Kanban não é renderizado — então ali o card só
	// chega ao usuário como item do menu. "search" e "return" seguem só pelo
	// recolhimento: a lupa já tem ícone próprio no fechado e "Retornar" é
	// transição de status, que não vale para chamado encerrado.
	const collapsedMenuItems = [
		(hidden.has("kanban") || (ticket.status === "closed" && showKanban && hasRealTicket)) && {
			key: "kanban",
			label: i18n.t("ticketActionButtons.tooltips.createKanbanCard"),
			onClick: handleOpenKanbanModal,
		},
		hidden.has("search") && {
			key: "search",
			label: i18n.t("ticketActionButtons.tooltips.searchMessages"),
			onClick: () => onSearchClick(),
		},
		hidden.has("return") && {
			key: "return",
			label: i18n.t("messagesList.header.buttons.return"),
			onClick: () => handleUpdateTicketStatus(null, "pending", null),
		},
		hidden.has("call") && {
			key: "call",
			label: i18n.t("ticketActionButtons.tooltips.call"),
			onClick: handleStartCall,
		},
	].filter(Boolean);

	const callButton = showCall && !hidden.has("call") && (
		<Tooltip title={i18n.t(inCallWithTicket ? "ticketActionButtons.tooltips.inCall" : "ticketActionButtons.tooltips.call")}>
			<IconButton onClick={handleStartCall} color={inCallWithTicket ? "secondary" : "default"}>
				<CallIcon />
			</IconButton>
		</Tooltip>
	);

	return (
		<div className={classes.actionButtons} ref={toolbarRef}>
			{closeGuardDialog}
			{closeRulesDialogs}
			<CreateKanbanCardModal
				open={kanbanModalOpen}
				onClose={handleCloseKanbanModal}
				ticket={ticket}
			/>

			{ticket.status === "closed" && canReopen && hasRealTicket && !isInactive && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{/* Recolher é por DESMONTAGEM, nunca display:none — um IconButton
			    escondido ainda ocuparia a margem do `& > *`, e o Tooltip pode
			    deixar popper órfão ancorado num nó invisível. Também não pode
			    entrar wrapper nenhum aqui: ele herdaria os 4px do `& > *` e
			    mudaria o espaçamento. */}
			{showSearch && !hidden.has("search") && (
				<Tooltip title={i18n.t("ticketActionButtons.tooltips.searchMessages")}>
					<IconButton onClick={onSearchClick}>
						<SearchIcon />
					</IconButton>
				</Tooltip>
			)}
			{ticket.status === "closed" && callButton}
			{ticket.status === "open" && (
				<>
					{callButton}
					{showKanban && !hidden.has("kanban") && (
						<Tooltip title={i18n.t("ticketActionButtons.tooltips.createKanbanCard")}>
							<IconButton onClick={handleOpenKanbanModal} color="primary">
								<DashboardIcon />
							</IconButton>
						</Tooltip>
					)}
					{!hidden.has("transfer") && (
						<Tooltip title={i18n.t("ticketOptionsMenu.transfer")}>
							<IconButton onClick={handleOpenTransferModal}>
								<SwapHorizIcon />
							</IconButton>
						</Tooltip>
					)}
					{!hidden.has("return") && (
						<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
							<IconButton onClick={e => handleUpdateTicketStatus(e, "pending", null)}>
								<UndoRoundedIcon />
							</IconButton>
						</Tooltip>
					)}
					<ThemeProvider theme={customTheme}>
						<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
							<IconButton onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)} color="primary">
								<CheckCircleIcon />
							</IconButton>
						</Tooltip>
					</ThemeProvider>
				</>
			)}
			{/* Fora do bloco de "open" para valer também no chamado fechado, mas
			    depois dele: o "..." é sempre o último ícone da barra (e é a âncora
			    do Menu, por isso nunca recolhe — ver PINNED_SLOTS). */}
			{showOptionsMenu && (
				<>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						extraItems={collapsedMenuItems}
						hasRealTicket={hasRealTicket}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<>
					{callButton}
					{canTransferPending && (
						<Tooltip title={i18n.t("ticketOptionsMenu.transfer")}>
							<IconButton onClick={handleOpenTransferModal}>
								<SwapHorizIcon />
							</IconButton>
						</Tooltip>
					)}
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						style={{ backgroundColor: "#f44336", color: "#fff" }}
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
				</>
			)}
			{(ticket.status === "open" || canTransferPending) && (
				<TransferTicketModalCustom
					modalOpen={transferTicketModalOpen}
					onClose={handleCloseTransferModal}
					ticketid={ticket.id}
					ticket={ticket}
				/>
			)}
		</div>
	);
};

export default TicketActionButtonsCustom;
